import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { fetchAllPages } from './export.ts';
import { admin } from './queries.ts';

export interface CustomerQuery {
  search?: string;
  blocked?: string;
  hasOrdered?: string;
  sort?: string;
  page?: number;
  pageSize?: number;
}

/** The filters as the API takes them — shared by the list and its export, so the two agree. */
function filters(query: CustomerQuery) {
  return {
    search: query.search || undefined,
    // The filter is tri-state in the URL — absent, "true", "false" — and only the last two
    // narrow anything. Sending `false` for "absent" would hide every blocked account.
    blocked: query.blocked === '' || query.blocked === undefined ? undefined : query.blocked === 'true',
    hasOrdered: query.hasOrdered === 'true' ? true : undefined,
    sort: query.sort || undefined,
  };
}

export function useCustomers(query: CustomerQuery) {
  return useQuery({
    queryKey: ['customers', 'list', query],
    queryFn: () =>
      admin().customers({ ...filters(query), page: query.page ?? 1, pageSize: query.pageSize ?? 20 }),
  });
}

/** Every customer the filters match, in the chosen order, for an export. */
export function allCustomers(query: CustomerQuery) {
  return fetchAllPages((page, pageSize) => admin().customers({ ...filters(query), page, pageSize }));
}

export function useCustomer(id: number) {
  return useQuery({
    queryKey: ['customers', 'one', id],
    queryFn: () => admin().customer(id),
  });
}

function useCustomerInvalidation() {
  const queryClient = useQueryClient();

  return () => {
    void queryClient.invalidateQueries({ queryKey: ['customers'] });
    void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
  };
}

export function useBlockCustomer(id: number) {
  const queryClient = useQueryClient();
  const invalidate = useCustomerInvalidation();

  return useMutation({
    mutationFn: ({ blocked, reason }: { blocked: boolean; reason?: string }) =>
      blocked ? admin().blockCustomer(id, { reason: reason || null }) : admin().unblockCustomer(id),
    onSuccess: (customer) => {
      queryClient.setQueryData(['customers', 'one', id], customer);
      invalidate();
    },
  });
}

/** Sends an unconfirmed customer a fresh confirmation link. Nothing on the record changes. */
export function useResendConfirmation(id: number) {
  return useMutation({ mutationFn: () => admin().resendCustomerConfirmation(id) });
}

export function useDeleteCustomer() {
  const invalidate = useCustomerInvalidation();

  return useMutation({
    mutationFn: (id: number) => admin().deleteCustomer(id),
    onSuccess: invalidate,
  });
}
